import Vue, { type VNode, type CreateElement } from 'vue';
import { PiniaVuePlugin, createPinia, setActivePinia, type Pinia } from 'pinia';
import App from '@/App.vue';
import { installConfigRuntime } from '@/runtime/config/install-config.runtime';
import { installBundleReadyRuntime } from '@/runtime/core/install-bundle-ready.runtime';
import { installImageRuntime } from '@/runtime/image/install-image.runtime';
import { installLoadingStoreRuntime } from '@/runtime/store/install-loading-store.runtime';
import { installUtilsRuntime } from '@/runtime/utils/install-utils.runtime';

const GH_RUNTIME_KEY = '__GH_RUNTIME__';

type GhRuntimeRegistry = {
    pinia?: Pinia;
    bridgesInstalled?: boolean;
    appInstances?: Record<string, Vue>;
    appMountTasks?: Record<string, Promise<Vue | null>>;
};

type GlobalWithGhRuntime = typeof globalThis & {
    [GH_RUNTIME_KEY]?: GhRuntimeRegistry;
};

type MountAppOptions = {
    selector?: string;
};

type MountVuePageOptions = {
    selector: string;
    render: (h: CreateElement) => VNode;
    appKey?: string;
};

const getRuntimeRegistry = (): GhRuntimeRegistry => {
    const globalRef = globalThis as GlobalWithGhRuntime;
    globalRef[GH_RUNTIME_KEY] ??= {
        appInstances: {},
        appMountTasks: {},
    };

    globalRef[GH_RUNTIME_KEY]!.appInstances ??= {};
    globalRef[GH_RUNTIME_KEY]!.appMountTasks ??= {};

    return globalRef[GH_RUNTIME_KEY]!;
};

/**
 * 安裝共用 Vue / Pinia runtime，供各入口重用
 */
export const ensureEntryRuntime = (): Pinia => {
    const registry = getRuntimeRegistry();

    if (registry.pinia) {
        setActivePinia(registry.pinia);
        return registry.pinia;
    }

    Vue.use(PiniaVuePlugin);

    const pinia = createPinia();
    registry.pinia = pinia;

    setActivePinia(pinia);

    return pinia;
};

/**
 * 安裝共用 runtime bridge，供 Layout 入口使用
 */
export const installSharedRuntimeBridges = (): void => {
    ensureEntryRuntime();

    const registry = getRuntimeRegistry();
    if (registry.bridgesInstalled) {
        return;
    }

    installUtilsRuntime();
    installLoadingStoreRuntime();
    installConfigRuntime();
    installImageRuntime();
    installBundleReadyRuntime();

    registry.bridgesInstalled = true;
};

/**
 * 掛載專案根元件 (App)
 */
export const mountApp = async ({
    selector = '#app',
}: MountAppOptions = {}): Promise<Vue | null> => {
    return mountVuePage({
        selector,
        appKey: `app:${selector}`,
        render: (h) => h(App),
    });
};

/**
 * 掛載任意頁面 Vue 元件
 */
export const mountVuePage = async ({
    selector,
    render,
    appKey,
}: MountVuePageOptions): Promise<Vue | null> => {
    const pinia = ensureEntryRuntime();
    const registry = getRuntimeRegistry();
    const resolvedAppKey = appKey ?? `page:${selector}`;

    const existedInstance = registry.appInstances?.[resolvedAppKey];
    if (existedInstance) {
        return existedInstance;
    }

    const existedTask = registry.appMountTasks?.[resolvedAppKey];
    if (existedTask) {
        return existedTask;
    }

    const mountTask = (async () => {
        try {
            const mountElement = document.querySelector(selector);
            if (!(mountElement instanceof HTMLElement)) {
                return null;
            }

            const instance = new Vue({
                pinia,
                render,
            }).$mount(mountElement);

            registry.appInstances![resolvedAppKey] = instance;
            return instance;
        } finally {
            delete registry.appMountTasks![resolvedAppKey];
        }
    })();

    registry.appMountTasks![resolvedAppKey] = mountTask;
    return mountTask;
};
